import React, { useState, useRef, useEffect } from "react";
import "../styles/KpiSettingsModal.css";
import {
  MdBarChart, MdShowChart, MdAreaChart, MdPieChart, MdDonutLarge,
  MdRadar, MdBubbleChart, MdGridView, MdFilterList, MdStackedBarChart,
  MdMultilineChart, MdScatterPlot,
} from "react-icons/md";
import {
  FaIdCard, FaMale, FaBirthdayCake, FaUtensils, FaCar, FaPaw, FaUser,
} from "react-icons/fa";

export const ALL_CHARTS = [
  { value: "bar",           label: "Bar",           Icon: MdBarChart },
  { value: "column",        label: "Column",        Icon: MdStackedBarChart },
  { value: "line",          label: "Line",          Icon: MdShowChart },
  { value: "area",          label: "Area",          Icon: MdAreaChart },
  { value: "stackedbar",    label: "Stacked Bar",   Icon: MdBarChart },
  { value: "stackedcolumn", label: "Stacked Column",Icon: MdStackedBarChart },
  { value: "pie",           label: "Pie",           Icon: MdPieChart },
  { value: "doughnut",      label: "Doughnut",      Icon: MdDonutLarge },
  { value: "radar",         label: "Radar",         Icon: MdRadar },
  { value: "radialbar",     label: "Radial Bar",    Icon: MdMultilineChart },
  { value: "scatter",       label: "Scatter",       Icon: MdScatterPlot },
  { value: "composed",      label: "Composed",      Icon: MdMultilineChart },
  { value: "funnel",        label: "Funnel",        Icon: MdFilterList },
  { value: "treemap",       label: "Treemap",       Icon: MdGridView },
  { value: "pictogram",     label: "Pictogram",     Icon: FaIdCard },
];

const CHART_GROUPS = [
  { group: "Basic",     charts: ALL_CHARTS.filter(c => ["bar","column","line","area"].includes(c.value)) },
  { group: "Stacked",   charts: ALL_CHARTS.filter(c => ["stackedbar","stackedcolumn"].includes(c.value)) },
  { group: "Circular",  charts: ALL_CHARTS.filter(c => ["pie","doughnut","radar","radialbar"].includes(c.value)) },
  { group: "Advanced",  charts: ALL_CHARTS.filter(c => ["scatter","composed","funnel","treemap"].includes(c.value)) },
  { group: "Pictogram", charts: ALL_CHARTS.filter(c => ["pictogram"].includes(c.value)) },
];

// Admin type values consumed by the dashboard viewer when chartType === "pictogram"
export const ADMIN_TYPES = [
  { value: "Card",         label: "Stat Card",       Icon: FaIdCard,       behavior: "One card per row with %, label, n=" },
  { value: "ShapeGender",  label: "Shape – Gender",  Icon: FaMale,         behavior: 'Matches row label text ("male"/"female"/"mann"/"frau" etc.) to pick Male vs Female icon, fills each by %' },
  { value: "ShapeAge",     label: "Shape – Age",     Icon: FaBirthdayCake, behavior: "Same cake icon for every row, filled by %, label shows the age bracket" },
  { value: "ShapeFood",    label: "Shape – Food",    Icon: FaUtensils,     behavior: "Generic food icon per row, filled by %" },
  { value: "ShapeVehicle", label: "Shape – Vehicle", Icon: FaCar,          behavior: "Generic car icon per row, filled by %" },
  { value: "ShapeAnimal",  label: "Shape – Animal",  Icon: FaPaw,          behavior: "Generic paw icon per row, filled by %" },
  { value: "ShapePerson",  label: "Shape – Person",  Icon: FaUser,         behavior: "Generic fallback person icon, filled by %" },
];

function ChartTypeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = ALL_CHARTS.find(c => c.value === value) ?? ALL_CHARTS[0];
  const SelectedIcon = selected.Icon;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="ksm-chart-dropdown">
      <button className="ksm-chart-trigger" onClick={() => setOpen(o => !o)}>
        <span className="ksm-chart-trigger-icon"><SelectedIcon size={14} /></span>
        <span className="ksm-chart-trigger-label">{selected.label}</span>
        <span className="ksm-chart-trigger-arrow">▾</span>
      </button>

      {open && (
        <div className="ksm-chart-menu">
          {CHART_GROUPS.map((group, gi) => (
            <div key={gi}>
              {gi > 0 && <div className="ksm-chart-divider" />}
              <div className="ksm-chart-group-label">{group.group}</div>
              {group.charts.map(({ value: cv, label, Icon }) => (
                <button
                  key={cv}
                  className={`ksm-chart-option${value === cv ? " ksm-chart-option--active" : ""}`}
                  onClick={() => { onChange(cv); setOpen(false); }}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const DEFAULT_KPI_SETTINGS = {
  showPercent: true,
  showFrequency: false,
  showTable: false,
  decimalPlaces: 1,
  paletteIndex: 0,
  chartType: "bar",
  adminType: null,
  downloads: { pdf: true, csv: true, ppt: false, xlsx: false },
};

export const PRESET_PALETTES = [
  { name: "Indigo",    colors: ["#6366f1","#8b5cf6","#a78bfa","#c4b5fd","#818cf8","#4f46e5"] },
  { name: "Ocean",     colors: ["#06b6d4","#0891b2","#0284c7","#0ea5e9","#38bdf8","#0369a1"] },
  { name: "Sunset",    colors: ["#f43f5e","#f97316","#fbbf24","#fb923c","#e11d48","#dc2626"] },
  { name: "Forest",    colors: ["#10b981","#059669","#34d399","#6ee7b7","#065f46","#16a34a"] },
  { name: "Corporate", colors: ["#334155","#475569","#64748b","#94a3b8","#1e293b","#0f172a"] },
];

const DOWNLOAD_OPTIONS = [
  { key: "pdf",  label: "PDF" },
  { key: "csv",  label: "CSV" },
  { key: "ppt",  label: "PPT" },
  { key: "xlsx", label: "XLSX" },
];

const scopeLabel = {
  global: "Global KPI Settings",
  tab:    "Tab KPI Settings",
  kpi:    "KPI Settings",
};

export default function KpiSettingsModal({ scope, title, currentSettings, onSave, onClose }) {
  const merged = { ...DEFAULT_KPI_SETTINGS, ...currentSettings };
  const [s, setS] = useState(merged);

  const set = (field, value) => setS((prev) => ({ ...prev, [field]: value }));

  const setDownload = (key, value) =>
    setS((prev) => ({ ...prev, downloads: { ...prev.downloads, [key]: value } }));

  const handleReset = () => setS({ ...DEFAULT_KPI_SETTINGS });

  return (
    <div className="ksm-overlay" onClick={onClose}>
      <div className="ksm-panel" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="ksm-header">
          <div className="ksm-header-left">
            <span className="ksm-scope-badge ksm-scope-badge--{scope}">{scopeLabel[scope]}</span>
            {title && <span className="ksm-title-text">{title}</span>}
          </div>
          <button className="ksm-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="ksm-body">

          {/* ── Chart Type ── */}
          <section className="ksm-section">
            <h4 className="ksm-section-title">Chart Type</h4>
            <ChartTypeDropdown value={s.chartType} onChange={(v) => set("chartType", v)} />
          </section>

          {/* ── Admin Type (only for Pictogram charts) ── */}
          {s.chartType === "pictogram" && (
            <section className="ksm-section">
              <h4 className="ksm-section-title">Admin Type</h4>
              <div className="ksm-palettes">
                {ADMIN_TYPES.map(({ value, label, Icon, behavior }) => (
                  <button
                    key={value}
                    className={`ksm-palette ${s.adminType === value ? "ksm-palette--active" : ""}`}
                    onClick={() => set("adminType", value)}
                    title={behavior}
                  >
                    <Icon size={20} />
                    <span className="ksm-palette-name">{label}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* ── Display ── */}
          <section className="ksm-section">
            <h4 className="ksm-section-title">Display</h4>
            <div className="ksm-row">
              <ToggleRow
                label="Show %"
                checked={s.showPercent}
                onChange={(v) => set("showPercent", v)}
              />
              <ToggleRow
                label="Show Frequency"
                checked={s.showFrequency}
                onChange={(v) => set("showFrequency", v)}
              />
              <ToggleRow
                label="Show Data Table"
                checked={s.showTable}
                onChange={(v) => set("showTable", v)}
              />
            </div>
            <div className="ksm-field-row">
              <label className="ksm-label">Decimal Places</label>
              <input
                type="number"
                className="ksm-number"
                min={0}
                max={4}
                value={s.decimalPlaces}
                onChange={(e) => set("decimalPlaces", Math.min(4, Math.max(0, Number(e.target.value))))}
              />
            </div>
          </section>

          {/* ── Colors ── */}
          <section className="ksm-section">
            <h4 className="ksm-section-title">Color Palette</h4>
            <div className="ksm-palettes">
              {PRESET_PALETTES.map((palette, idx) => (
                <button
                  key={idx}
                  className={`ksm-palette ${s.paletteIndex === idx ? "ksm-palette--active" : ""}`}
                  onClick={() => set("paletteIndex", idx)}
                  title={palette.name}
                >
                  <div className="ksm-palette-swatches">
                    {palette.colors.slice(0, 4).map((c, ci) => (
                      <span key={ci} className="ksm-swatch" style={{ background: c }} />
                    ))}
                  </div>
                  <span className="ksm-palette-name">{palette.name}</span>
                </button>
              ))}
            </div>
          </section>

          {/* ── Downloads ── */}
          <section className="ksm-section">
            <h4 className="ksm-section-title">
              Downloads
              <span className="ksm-section-hint"> — enabled in live dashboard</span>
            </h4>
            <div className="ksm-downloads">
              {DOWNLOAD_OPTIONS.map(({ key, label }) => (
                <label key={key} className="ksm-download-opt">
                  <input
                    type="checkbox"
                    checked={!!s.downloads[key]}
                    onChange={(e) => setDownload(key, e.target.checked)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="ksm-footer">
          <button className="ksm-btn ksm-btn--ghost" onClick={handleReset}>
            Reset to defaults
          </button>
          <div className="ksm-footer-right">
            <button className="ksm-btn ksm-btn--cancel" onClick={onClose}>Cancel</button>
            <button className="ksm-btn ksm-btn--apply" onClick={() => onSave(s)}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <label className="ksm-toggle-row">
      <span className="ksm-toggle-label">{label}</span>
      <div
        className={`ksm-toggle ${checked ? "ksm-toggle--on" : ""}`}
        onClick={() => onChange(!checked)}
      >
        <div className="ksm-toggle-thumb" />
      </div>
    </label>
  );
}
